import axios from 'axios';
import { User, Week, Game, Pick, PickSummary, PickWithGameInfo, ParticipationStatus, TiebreakerGame, TiebreakerPrediction, SeasonStanding, WeeklyStanding, WeeklyWinner, StandingsSummary, Sport, CombinedStandings } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  login: async (username: string, password: string) => {
    const response = await api.post('/auth/login', { username, password });
    return response.data;
  },
  
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  
  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  }
};

// Users API
export const usersAPI = {
  getAll: async (): Promise<User[]> => {
    const response = await api.get('/users');
    return response.data;
  },
  
  getById: async (id: number): Promise<User> => {
    const response = await api.get(`/users/${id}`);
    return response.data;
  },

  getProfile: async (): Promise<User> => {
    const response = await api.get('/users/profile');
    return response.data;
  },

  updateProfile: async (email: string): Promise<{ message: string; email: string }> => {
    const response = await api.put('/users/profile', { email });
    return response.data;
  },

  changePassword: async (currentPassword: string, newPassword: string): Promise<{ message: string }> => {
    const response = await api.put('/users/password', { currentPassword, newPassword });
    return response.data;
  }
};

// Weeks API
export const weeksAPI = {
  getAll: async (params?: { sport?: Sport }): Promise<Week[]> => {
    const queryString = params?.sport ? `?sport=${params.sport}` : '';
    const response = await api.get(`/weeks${queryString}`);
    return response.data;
  },

  getWeeks: async (params?: { sport?: Sport }): Promise<Week[]> => {
    const queryString = params?.sport ? `?sport=${params.sport}` : '';
    const response = await api.get(`/weeks${queryString}`);
    return response.data;
  },
  
  getCurrent: async (): Promise<Week | null> => {
    const response = await api.get('/weeks/current');
    return response.data;
  },

  canCreateNext: async (): Promise<{
    canCreate: boolean;
    reason: string;
    message: string;
    lastWinner?: {
      username: string;
      week_number: number;
      year: number;
    };
    nextWeek?: {
      week_number: number;
      year: number;
    };
  }> => {
    const response = await api.get('/weeks/can-create-next');
    return response.data;
  },
  
  create: async (weekData: {
    week_number: number;
    year: number;
    sport: Sport;
    picks_lock_time: string;
  }): Promise<Week> => {
    const response = await api.post('/weeks', weekData);
    return response.data;
  },
  
  finalize: async (weekId: number) => {
    const response = await api.post(`/weeks/${weekId}/finalize`);
    return response.data;
  },

  updateLockTime: async (weekId: number) => {
    const response = await api.post(`/weeks/${weekId}/update-lock-time`);
    return response.data;
  },

  lockPicks: async (weekId: number) => {
    const response = await api.post(`/weeks/${weekId}/lock-picks`);
    return response.data;
  }
};

// Games API
export const gamesAPI = {
  getByWeek: async (weekId: number): Promise<Game[]> => {
    const response = await api.get(`/games/week/${weekId}`);
    return response.data;
  },

  getGamesForWeek: async (weekId: number): Promise<Game[]> => {
    const response = await api.get(`/games/week/${weekId}`);
    return response.data;
  },
  
  create: async (gameData: {
    week_id: number;
    home_team: string;
    away_team: string;
    spread?: number;
    game_time: string;
    is_spread_pick?: boolean;
  }): Promise<Game> => {
    const response = await api.post('/games', gameData);
    return response.data;
  },
  
  update: async (gameId: number, gameData: Partial<Game>): Promise<void> => {
    const response = await api.put(`/games/${gameId}`, gameData);
    return response.data;
  },
  
  delete: async (gameId: number): Promise<void> => {
    const response = await api.delete(`/games/${gameId}`);
    return response.data;
  },

  updateSpread: async (gameId: number, spread: number) => {
    const response = await api.put(`/games/${gameId}/spread`, { spread });
    return response.data;
  }
};

// Picks API
export const picksAPI = {
  getPicksForWeek: async (weekId: number): Promise<Pick[]> => {
    const response = await api.get(`/picks/week/${weekId}`);
    return response.data;
  },
  
  getAllPicksForWeek: async (weekId: number): Promise<PickWithGameInfo[]> => {
    const response = await api.get(`/picks/week/${weekId}/all`);
    return response.data;
  },
  
  submitPick: async (gameId: number, selectedTeam: string): Promise<Pick> => {
    const response = await api.post('/picks', {
      game_id: gameId,
      selected_team: selectedTeam
    });
    return response.data;
  },

  submitMultiplePicks: async (picks: Array<{game_id: number, selected_team: string}>) => {
    const response = await api.post('/picks/bulk', { picks });
    return response.data;
  },

  deletePick: async (gameId: number) => {
    const response = await api.delete(`/picks/${gameId}`);
    return response.data;
  },
  
  getPicksSummary: async (weekId: number): Promise<PickSummary[]> => {
    const response = await api.get(`/picks/week/${weekId}/summary`);
    return response.data;
  },

  getParticipation: async (weekId: number): Promise<ParticipationStatus[]> => {
    const response = await api.get(`/picks/week/${weekId}/participation`);
    return response.data;
  }
};

// Schedule API
export const scheduleAPI = {
  getCurrentWeek: async (sport: Sport = 'football') => {
    const response = await api.get(`/schedule/current-week/${sport}`);
    return response.data;
  },

  getGames: async (sport: Sport, year: number, week: number) => {
    const response = await api.get(`/schedule/games/${sport}/${year}/${week}`);
    return response.data;
  },

  getGamesWithLines: async (sport: Sport, year: number, week: number) => {
    const response = await api.get(`/schedule/games/${sport}/${year}/${week}/with-lines`);
    return response.data;
  },

  searchGames: async (sport: Sport, year: number, week: number, team?: string) => {
    const params = team ? `?team=${encodeURIComponent(team)}` : '';
    const response = await api.get(`/schedule/search/${sport}/${year}/${week}${params}`);
    return response.data;
  },

  getGamesByDate: async (sport: Sport, date: string) => {
    const response = await api.get(`/schedule/games/${sport}/by-date/${date}`);
    return response.data;
  }
};

// Tiebreakers API
export const tiebreakersAPI = {
  getTiebreakers: async (weekId: number): Promise<TiebreakerGame[]> => {
    const response = await api.get(`/tiebreakers/week/${weekId}`);
    return response.data;
  },

  setTiebreakers: async (weekId: number, tiebreaker1GameId: number, tiebreaker2GameId: number): Promise<{ message: string }> => {
    const response = await api.post(`/tiebreakers/week/${weekId}`, {
      tiebreaker1GameId,
      tiebreaker2GameId
    });
    return response.data;
  },

  getPredictions: async (weekId: number): Promise<TiebreakerPrediction[]> => {
    const response = await api.get(`/tiebreakers/week/${weekId}/predictions`);
    return response.data;
  },

  submitPredictions: async (weekId: number, predictions: Array<{tiebreaker_game_id: number, predicted_total_points: number}>): Promise<{ message: string }> => {
    const response = await api.post(`/tiebreakers/week/${weekId}/predictions`, { predictions });
    return response.data;
  },

  getAllPredictions: async (weekId: number): Promise<TiebreakerPrediction[]> => {
    const response = await api.get(`/tiebreakers/week/${weekId}/all-predictions`);
    return response.data;
  }
};

// Standings API
export const standingsAPI = {
  getSeason: async (year: number, sport: Sport): Promise<SeasonStanding[]> => {
    const response = await api.get(`/standings/season/${year}/${sport}`);
    return response.data;
  },

  getSeasonStandings: async (year: number, sport: Sport = 'football'): Promise<SeasonStanding[]> => {
    const response = await api.get(`/standings/season/${year}/${sport}`);
    return response.data;
  },

  getCombined: async (year: number): Promise<CombinedStandings[]> => {
    const response = await api.get(`/standings/combined/${year}`);
    return response.data;
  },

  getCurrentSeasonStandings: async (): Promise<SeasonStanding[]> => {
    const response = await api.get('/standings/current');
    return response.data;
  },

  getWeeklyStandings: async (weekId: number): Promise<WeeklyStanding[]> => {
    const response = await api.get(`/standings/week/${weekId}`);
    return response.data;
  },

  getWeeklyWinners: async (year: number, sport?: Sport): Promise<WeeklyWinner[]> => {
    const url = sport
      ? `/standings/weekly-winners/${year}/${sport}`
      : `/standings/weekly-winners/${year}`;
    const response = await api.get(url);
    return response.data;
  },

  getStandingsSummary: async (year: number): Promise<StandingsSummary> => {
    const response = await api.get(`/standings/summary/${year}`);
    return response.data;
  }
};


// Admin API
export const adminAPI = {
  getDashboard: async () => {
    const response = await api.get('/admin/dashboard');
    return response.data;
  },

  lockPicks: async () => {
    const response = await api.post('/admin/lock-picks');
    return response.data;
  },

  unlockPicks: async () => {
    const response = await api.post('/admin/unlock-picks');
    return response.data;
  },

  getCurrentWeekGames: async () => {
    const response = await api.get('/admin/current-week-games');
    return response.data;
  },

  updateGameScores: async (gameId: number, homeScore: number, awayScore: number, isFinal: boolean) => {
    const response = await api.put(`/admin/games/${gameId}/scores`, {
      home_score: homeScore,
      away_score: awayScore,
      is_final: isFinal
    });
    return response.data;
  },

  calculateResults: async () => {
    const response = await api.post('/admin/calculate-results');
    return response.data;
  },

  updateRankings: async () => {
    const response = await api.post('/admin/update-rankings');
    return response.data;
  },

  getCurrentRankings: async () => {
    const response = await api.get('/admin/current-rankings');
    return response.data;
  }
};

// Rankings API
export const rankingsAPI = {
  updateRankings: async () => {
    const response = await api.post('/rankings/update');
    return response.data;
  },

  getCurrentRankings: async () => {
    const response = await api.get('/rankings/current');
    return response.data;
  },

  getLiveRankings: async (sport: Sport, poll: string = 'ap') => {
    const response = await api.get(`/rankings/live/${sport}`, {
      params: { poll }
    });
    return response.data;
  }
};

export default api;
